import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getAuthHeaders } from '../utils/api';

const LogbookContext = createContext();

export const LogbookProvider = ({ children }) => {
  const { userId, isLoggedIn } = useAuth();
  const [logbooks, setLogbooks] = useState([]);
  const [currentLogbookId, setCurrentLogbookIdState] = useState(null);
  const [loading, setLoading] = useState(false);
  const logbooksRef = useRef(logbooks);
  logbooksRef.current = logbooks;

  const fetchLogbooks = useCallback(async () => {
    if (!isLoggedIn || !userId) {
      setLogbooks([]);
      setCurrentLogbookIdState(null);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/logbooks`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setLogbooks(list);
        setCurrentLogbookIdState((prev) => {
          if (prev && list.some((lb) => lb._id === prev)) return prev;
          return list.length > 0 ? list[0]._id : null;
        });
      } else {
        setLogbooks([]);
        setCurrentLogbookIdState(null);
      }
    } catch (err) {
      console.error('Error fetching logbooks:', err);
      setLogbooks([]);
      setCurrentLogbookIdState(null);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, userId]);

  // Only allow setting to a logbook id that exists in the current list (never e.g. invitation code)
  const setCurrentLogbookId = useCallback((id) => {
    const next = id === undefined || id === null || id === '' ? null : id;
    if (next === null) {
      setCurrentLogbookIdState(null);
      return;
    }
    setCurrentLogbookIdState((prev) => {
      const list = logbooksRef.current;
      if (Array.isArray(list) && list.some((lb) => lb._id === next)) return next;
      return prev;
    });
  }, []);

  useEffect(() => {
    fetchLogbooks();
  }, [fetchLogbooks]);

  const value = {
    logbooks,
    currentLogbookId,
    setCurrentLogbookId,
    refreshLogbooks: fetchLogbooks,
    loading,
    hasLogbooks: logbooks.length > 0
  };

  return (
    <LogbookContext.Provider value={value}>
      {children}
    </LogbookContext.Provider>
  );
};

export const useLogbook = () => useContext(LogbookContext);
